import { useEffect, useSyncExternalStore, type AnchorHTMLAttributes, type MouseEvent } from 'react'

/* ===========================================================================
 *  Waar ben je
 *
 *  Een eigen router van veertig regels in plaats van een bibliotheek. Er
 *  zijn hier een handvol schermen en geen geneste lay-outs; wat er nodig
 *  is, is dat de adresbalk klopt (zodat je een gesprek kunt terugvinden en
 *  doorsturen) en dat de terugknop werkt. Dat is history.pushState en een
 *  luisteraar.
 *
 *  De adressen
 *  -----------
 *
 *    /                         een nieuw gesprek
 *    /c/:id                    een bestaand gesprek
 *    /taken                    de terugkerende taken
 *    /taken/nieuw              een nieuwe terugkerende taak (?sjabloon=...)
 *    /taken/:id                een terugkerende taak bewerken
 *    /over                     wat Cowork kan, en hoe het met rechten zit
 * =========================================================================== */

type Luisteraar = () => void
const luisteraars = new Set<Luisteraar>()

function melden() { for (const l of luisteraars) l() }

window.addEventListener('popstate', melden)

export function navigeer(naar: string, vervang = false) {
  if (naar === location.pathname + location.search) return
  if (vervang) history.replaceState(null, '', naar)
  else history.pushState(null, '', naar)
  melden()
  /* Een nieuw scherm begint bovenaan; bij terug houdt de browser zelf de
     plek vast. */
  if (!vervang) document.getElementById('inhoud')?.scrollTo(0, 0)
}

/** Een zoekparameter zetten zonder een nieuwe stap in de geschiedenis. */
export function zetParam(naam: string, waarde: string | null) {
  const u = new URL(location.href)
  if (waarde === null) u.searchParams.delete(naam)
  else u.searchParams.set(naam, waarde)
  history.replaceState(null, '', u.pathname + u.search)
  melden()
}

function abonneer(l: Luisteraar) {
  luisteraars.add(l)
  return () => { luisteraars.delete(l) }
}

const nuHier = () => location.pathname + location.search

export interface Route {
  pad: string
  delen: string[]
  q: URLSearchParams
}

export function useRoute(): Route {
  const volledig = useSyncExternalStore(abonneer, nuHier)
  const u = new URL(volledig, location.origin)
  return {
    pad: u.pathname,
    delen: u.pathname.split('/').filter(Boolean).map(decodeURIComponent),
    q: u.searchParams,
  }
}

/** Een gewone link die niet de hele pagina opnieuw laadt. */
export function Link({ naar, onClick, ...rest }: { naar: string } & AnchorHTMLAttributes<HTMLAnchorElement>) {
  function klik(e: MouseEvent<HTMLAnchorElement>) {
    onClick?.(e)
    if (e.defaultPrevented) return
    /* Ctrl/Cmd-klik, middelste knop: een nieuw tabblad, zoals de gebruiker
       verwacht. Niet onderscheppen. */
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
    e.preventDefault()
    navigeer(naar)
  }
  return <a href={naar} onClick={klik} {...rest} />
}

/* ------------------------------------------------------------------ *
 *  De kop van de pagina: in het tabblad én in de bovenbalk
 *
 *  Elk scherm roept useTitel aan; het tabblad en de dunne balk boven het
 *  werkvlak lezen het. Bij een gesprek is dat de titel van het gesprek,
 *  zoals in elke chat-app.
 * ------------------------------------------------------------------ */

let kop: { titel: string; sub: string } = { titel: '', sub: '' }
const kopLuisteraars = new Set<() => void>()

export function useTitel(titel: string | null | undefined, sub?: string | null) {
  useEffect(() => {
    document.title = titel ? `${titel} · Cowork` : 'Cowork · Truckwash 1 Group'
    if (titel === undefined) return
    kop = { titel: titel ?? '', sub: sub ?? '' }
    for (const l of kopLuisteraars) l()
  }, [titel, sub])
}

export function useKop() {
  return useSyncExternalStore(
    (l) => { kopLuisteraars.add(l); return () => { kopLuisteraars.delete(l) } },
    () => kop,
  )
}
